import React, { useEffect, useRef } from 'react';
import PropTypes from 'prop-types';
import BackNavigationButton from '@cvent/carina/components/ScrollViewWithBars/BackNavigationButton';
import DismissButton from '@cvent/carina/components/ScrollViewWithBars/DismissButton';
import ScrollViewWithBars from '@cvent/carina/components/ScrollViewWithBars/ScrollViewWithBars';
import { Row } from '@cvent/carina/components/Row';
import { Col } from '@cvent/carina/components/Col';
import useTheme from '@cvent/blocks/hooks/useTheme';
import { injectTestId } from '@cvent/nucleus-test-automation';
import { useTranslate } from 'nucleus-text';
import { PageAlert } from '@cvent/carina/components/Alert/PageAlert';
import lineClamp from '@tools/lineClamp';
import { getApptErrorKey } from '@components/appointments/utilities/getApptErrorKey';
import ApptPanelMenu from './ApptPanelMenu';
import { ApptFileUploadError } from './ApptFileUploadError';

const useStyles = (inWebView, closePanel, hasFooter) => {
  const { backgroundColor } = useTheme();
  return {
    title: {
      marginTop: 12,
      marginBottom: 10,
      width: '100%'
    },
    centeredTitle: {
      width: '100%',
      textAlign: 'center',
      marginTop: 12,
      marginRight: inWebView || !closePanel ? 40 : 0
    },
    content: {
      padding: 24,
      paddingTop: 0,
      // When there is a footer, we add padding to keep the content from being hidden by the sticky footer
      paddingBottom: hasFooter ? 72 : 0
    },
    backBtn: {
      cursor: 'pointer',
      marginTop: 10,
      paddingLeft: 0,
      marginLeft: -5
    },
    closeBtn: {
      cursor: 'pointer',
      marginTop: 10,
      paddingRight: 6
    },
    backgroundColor: backgroundColor.base
  };
};

export const panelBackBtnId = 'appt-panel-back';
export const panelMenuBtnId = 'appt-panel-menu';
export const panelCancelBtnId = 'appt-panel-close';

export const ApptPanel = ({
  closePanel,
  onBack,
  title,
  titleId,
  content,
  buttons,
  alerts,
  fileAlerts,
  menuData,
  inWebView
}) => {
  const styles = useStyles(inWebView, !!closePanel, buttons);
  const { translate } = useTranslate();
  const contentRef = useRef(null);
  const panelType = content?.type?.name;
  let footer;

  if (buttons) {
    footer = (
      <Row
        margin={{ top: 16, bottom: 16, start: 8, end: 8 }}
        justifyContent="flex-end"
      >
        <Col width="fill" flex={{ display: 'flex', alignItems: 'center' }}>
          {buttons}
        </Col>
      </Row>
    );
  }

  // focus on first button when panel opens
  useEffect(() => {
    if (title) {
      setTimeout(() => {
        let btn;
        if (onBack) {
          btn = document.getElementById(panelBackBtnId);
        } else if (menuData) {
          btn = document.getElementById(panelMenuBtnId);
        } else if (closePanel) {
          btn = document.getElementById(panelCancelBtnId);
        }
        if (btn) {
          btn.focus();
        }
      }, 0);
    }
  }, [title]); // eslint-disable-line react-hooks/exhaustive-deps

  // set top to 0 when panel opens
  useEffect(() => {
    const parentElement = contentRef?.current?.parentElement;
    if (parentElement) {
      parentElement.scrollTop = 0;
    }
  }, [panelType]);

  return (
    <ScrollViewWithBars
      {...injectTestId('appt-panel')}
      forceStickyFooter
      header={
        title && (
          <Row
            margin={{ top: 12, bottom: 12, start: inWebView ? 8 : 16, end: 8 }}
          >
            {onBack && (
              <Col width="content">
                <BackNavigationButton
                  id={panelBackBtnId}
                  css={styles.backBtn}
                  accessibilityLabel={translate('Universal-PanelGoBack')}
                  testID={panelBackBtnId}
                  onClick={onBack}
                />
              </Col>
            )}
            {fileAlerts && <ApptFileUploadError errors={fileAlerts} />}
            <Col width="fill" flex={{ display: 'flex' }}>
              <h2
                id={titleId}
                css={[
                  onBack ? styles.centeredTitle : lineClamp(6) && styles.title
                ]}
                {...injectTestId('appt-panel-title')}
              >
                {title}
              </h2>
            </Col>
            {menuData ? (
              <ApptPanelMenu id={panelMenuBtnId} {...menuData} />
            ) : null}
            {closePanel && !inWebView && (
              <Col width="content">
                <DismissButton
                  id={panelCancelBtnId}
                  css={styles.closeBtn}
                  accessibilityLabel={translate('AppointmentPanel-ClosePanel')}
                  testID="appt-panel-x"
                  onClick={() => closePanel()}
                />
              </Col>
            )}
          </Row>
        )
      }
      footer={footer}
      // Making the footer sticky at the bottom of the panel
      css={
        footer
          ? {
              overflow: 'clip',
              '& > div:last-of-type': {
                position: 'sticky',
                bottom: 0,
                background: styles.backgroundColor
              }
            }
          : {}
      }
    >
      {alerts?.length ? (
        <div
          css={{
            padding: 24,
            paddingTop: 0,
            paddingBottom: 30
          }}
          {...injectTestId('appt-panel-alerts')}
        >
          {alerts}
        </div>
      ) : null}
      <div style={styles.content} ref={contentRef}>
        {content}
      </div>
    </ScrollViewWithBars>
  );
};

export const getApptErrors = errors => {
  const apptErrors = [];
  errors.graphQLErrors.forEach(error => {
    const { errorInfo, message } = error;
    if (!errorInfo) {
      apptErrors.push({ id: Date.now(), message });
    } else if (errorInfo.additionalInfo?.errors) {
      apptErrors.push(...errorInfo.additionalInfo.errors);
    } else if (errorInfo.additionalInfo?.message) {
      apptErrors.push({
        id: errorInfo.requestId,
        message: errorInfo.additionalInfo.message
      });
    } else if (errorInfo.additionalInfo?.response?.errors) {
      errorInfo.additionalInfo.response.errors.forEach(e => {
        apptErrors.push({
          id: e.id,
          messageKey: e.resx,
          message: e.message
        });
      });
    } else if (message) {
      apptErrors.push({ id: errorInfo.requestId, message });
    } else {
      apptErrors.push({
        id: errorInfo.requestId,
        messageKey: 'Universal-Unknown-Error'
      });
    }
  });

  return apptErrors;
};

export const getPanelAlert = ({ err, errors, setErrors }) => {
  return (
    <PanelAlert
      key={err.id}
      {...{
        ...err,
        alerts: errors,
        setAlerts: setErrors
      }}
    />
  );
};

export const getPanelErrors = ({ error, setErrors, errors }) => {
  let errs = [];
  if (error.graphQLErrors?.length) {
    errs = getApptErrors(error);
  } else {
    errs = [{ id: Date.now(), messageKey: 'Universal-Unknown-Error' }];
  }
  if (errs.length) {
    setErrors(
      (errs[0].message || errs[0]?.messageKey ? errs : errs[0]).map(err =>
        getPanelAlert({ err, errors, setErrors })
      )
    );
  }
};

export const PanelAlert = ({
  message,
  messageKey,
  id,
  type = 'danger',
  alerts,
  setAlerts
}) => {
  const { translate } = useTranslate();
  const props = {
    id,
    testID: `alert-${type}-${id}`,
    appearance: type,
    content: translate(getApptErrorKey(messageKey)) || message,
    onDismiss: alertId => {
      if (!alerts.length) return;
      setAlerts(prevAlerts => {
        const indexToRemove = prevAlerts.findIndex(
          prevAlert => prevAlert.id === alertId
        );
        if (indexToRemove === -1) return prevAlerts;
        prevAlerts.splice(indexToRemove, 1);
        return [...prevAlerts];
      });
    },
    isRtl: false,
    dismissible: true
  };
  return <PageAlert {...props} />;
};

ApptPanel.propTypes = {
  closePanel: PropTypes.func,
  onBack: PropTypes.func,
  title: PropTypes.node,
  titleId: PropTypes.string,
  content: PropTypes.any,
  alerts: PropTypes.any,
  fileAlerts: PropTypes.arrayOf(
    PropTypes.shape({
      type: PropTypes.string,
      files: PropTypes.arrayOf(PropTypes.object)
    })
  ),
  buttons: PropTypes.oneOfType([PropTypes.array, PropTypes.node]),
  menuData: PropTypes.object,
  inWebView: PropTypes.bool
};

export default ApptPanel;
